import { Component, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Duration, DurationLikeObject, Settings } from 'luxon';
import { map, startWith } from 'rxjs';
import { TimeInputBoxComponent } from '../time-span/time-input-box/time-input-box.component';

@Component({
  selector: 'vectronic-formly-relative-time',
  imports: [TimeInputBoxComponent, ReactiveFormsModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: RelativeTimeComponent }],
  templateUrl: './relative-time.component.html',
  styleUrls: ['./relative-time.component.scss'],
})
export class RelativeTimeComponent implements ControlValueAccessor {
  private fb = inject(FormBuilder);

  relativeTimeForm = this.fb.group({
    weeks: this.fb.control<number>(0),
    days: this.fb.control<number>(0),
    hours: this.fb.control<number>(0),
    minutes: this.fb.control<number>(0),
  });

  maxUnitValues = {
    hours: 23,
    days: 6,
    minutes: 59,
  };

  maxDateRelative = input<string>();
  minDateRelative = input<string>();
  defaultValue = input<string>();
  label = input<string>();
  units = input<('weeks' | 'days' | 'hours' | 'minutes')[]>(['weeks', 'days', 'hours']);

  private maxDuration = computed(() =>
    this.maxDateRelative() ? Duration.fromISO(this.maxDateRelative()) : undefined,
  );
  private minDuration = computed(() =>
    this.minDateRelative() ? Duration.fromISO(this.minDateRelative()) : undefined,
  );

  maxDurationHumanReadable = computed(() =>
    this.maxDuration() ? this.maxDuration().toHuman() : undefined,
  );

  private formValueAsDuration$ = this.relativeTimeForm.valueChanges.pipe(
    startWith(this.relativeTimeForm.getRawValue()),
    map((formValue) =>
      Duration.fromObject({
        weeks: formValue.weeks || 0,
        days: formValue.days || 0,
        hours: formValue.hours || 0,
        minutes: formValue.minutes || 0,
      }),
    ),
  );
  private formValueAsDuration = toSignal(this.formValueAsDuration$);

  minWeeks = computed(() => this.computeMinUnitValue('weeks', this.formValueAsDuration()));
  maxWeeks = computed(() => this.computeMaxUnitValue('weeks', this.formValueAsDuration()));

  minDays = computed(() => this.computeMinUnitValue('days', this.formValueAsDuration()));
  maxDays = computed(() => this.computeMaxUnitValue('days', this.formValueAsDuration()));

  minHours = computed(() => this.computeMinUnitValue('hours', this.formValueAsDuration()));
  maxHours = computed(() => this.computeMaxUnitValue('hours', this.formValueAsDuration()));

  minMinutes = computed(() => this.computeMinUnitValue('minutes', this.formValueAsDuration()));
  maxMinutes = computed(() => this.computeMaxUnitValue('minutes', this.formValueAsDuration()));

  onChange: (value: string) => void;
  onTouched: () => void;

  constructor() {
    this.formValueAsDuration$
      .pipe(
        takeUntilDestroyed(),
        map((duration) => duration.toISO()),
      )
      .subscribe((duration) => {
        this.onChange?.(duration);
        this.onTouched?.();
      });
    Settings.defaultLocale = 'en';
  }
  writeValue(value: string): void {
    const durationObject = this.parseDurationString(value || this.defaultValue());
    this.relativeTimeForm.patchValue(durationObject);
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.relativeTimeForm.disable() : this.relativeTimeForm.enable();
  }

  getFormControl(controlName: string): FormControl {
    return this.relativeTimeForm.get(controlName) as FormControl;
  }

  private computeMaxUnitValue(unit: keyof DurationLikeObject, currentDuration: Duration): number {
    const max = this.maxUnitValues[unit];
    if (!this.maxDuration()) {
      return max;
    }
    const calculatedMax = Math.floor(
      currentDuration.get(unit) + this.maxDuration().minus(currentDuration).as(unit),
    );
    return max ? Math.min(max, calculatedMax) : calculatedMax;
  }

  private computeMinUnitValue(unit: keyof DurationLikeObject, currentDuration: Duration) {
    if (!this.minDuration() || !currentDuration) return 0;
    const durationDiffUnits = currentDuration.minus(this.minDuration()).as(unit);
    const currentDurationUnits = currentDuration.get(unit);
    return durationDiffUnits > currentDurationUnits
      ? 0
      : Math.ceil(currentDurationUnits - durationDiffUnits);
  }

  private parseDurationString(durationISOString: string) {
    const duration = Duration.fromISO(durationISOString);
    return {
      weeks: duration.weeks,
      days: duration.days,
      hours: duration.hours,
      minutes: duration.minutes,
    };
  }
}
